/**
 * The shop.app reference sits on a soft background tinted from the store's
 * own hero image — that's what makes the page read as designed rather than
 * assembled. This derives that tint CLIENT-SIDE from an already-loaded
 * `<img>`: draw it down to a tiny offscreen canvas, average the pixels to a
 * dominant colour, then desaturate and lighten it hard so the tint stays a
 * whisper, never a wash that would fight body-text contrast.
 *
 * Deliberately has no server dependency and no network fetch of its own —
 * it only ever reads pixels off an image the page already loaded for the
 * hero. Two things can make `getImageData` throw: a cross-origin image with
 * no CORS headers (canvas goes "tainted") and, in theory, an image that
 * failed to decode. Both are caught by `deriveCoverTint` and answered with
 * `null` — the caller's job is to fall back to the theme colour
 * (`--c-primary`) when this returns `null`, never to let the page break.
 */

/** Average RGB across an RGBA buffer (e.g. from `ImageData.data`). */
export function averageRgb(rgba) {
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    // Ignore fully transparent pixels — a padded/letterboxed image must not
    // let its transparent border drag the sample toward black.
    if (rgba[i + 3] === 0) continue;
    r += rgba[i];
    g += rgba[i + 1];
    b += rgba[i + 2];
    count += 1;
  }
  if (count === 0) return { r: 0, g: 0, b: 0 };
  return { r: r / count, g: g / count, b: b / count };
}

/** Standard RGB(0-255) → HSL(h:0-360, s:0-100, l:0-100). */
export function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)); break;
    case gn: h = ((bn - rn) / d + 2); break;
    default: h = ((rn - gn) / d + 4); break;
  }
  return { h: h * 60, s: s * 100, l: l * 100 };
}

/* ── The band ────────────────────────────────────────────────────────
 * The reference (shop.app) tints the WHOLE page with the cover's colour: a
 * brown store gets a genuinely brown page, white text on it. So lightness
 * FOLLOWS the source rather than being pinned to one "whisper" value — it
 * is only clamped away from the two extremes where nothing could be drawn
 * legibly, and saturation is muted so the result is a tint, not a wash.
 *
 * Because the band spans light AND dark, no single hardcoded foreground
 * works across it. `tintForeground` below picks one per tint, by measured
 * contrast — that is the whole reason these are exported constants rather
 * than magic numbers: the contrast guarantee is asserted across this exact
 * band in `utils/__tests__/coverTint.test.js`. */
export const TINT_MAX_SATURATION = 22;
export const TINT_MIN_LIGHTNESS = 26;
export const TINT_MAX_LIGHTNESS = 94;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Hue and (muted) saturation from the source; lightness from the source,
 * clamped into the legible band. */
export function rgbToTint(rgb) {
  const { h, s, l } = rgbToHsl(rgb);
  return {
    h: Math.round(((h % 360) + 360) % 360),
    s: Math.round(clamp(s, 0, TINT_MAX_SATURATION)),
    l: Math.round(clamp(l, TINT_MIN_LIGHTNESS, TINT_MAX_LIGHTNESS)),
  };
}

/**
 * THE GROUND, AS A TINT — WITHOUT THE PHOTOGRAPH SAMPLER'S REDUCTION.
 *
 * `rgbToTint` above exists for ONE input: the average of somebody's cover
 * photograph. A holiday snap can average to a fully saturated red at a
 * mid lightness, and painting a whole shop in it is a wash, not a tint — so
 * saturation is muted to `TINT_MAX_SATURATION` and lightness is clamped into
 * the legible band. That is right, and it stays.
 *
 * It is the wrong function for the other input, and applying it there is the
 * bug this exists to end. When no cover resolves, the ground is whatever
 * `Pages/Shop/styles.module.css` declared — the salon's own theme colour at
 * 14% over white, or 4% in LIGHT, or plain white in CLASSIC. That is ALREADY
 * a tint; it was authored as one. Reducing it again does real damage, because
 * HSL saturation is not perceptual chroma: at l = 93.5% the live shop's ground
 * is hsl(19, 88%, 93.5%) and carries a chroma of just 0.10, and clamping its
 * saturation to 22 cuts that to 0.03 — a near-neutral. Every surface then
 * derives from the near-neutral while the page stays painted the peach, which
 * is a taupe pill on a warm page: the "grey tabs" the owner reported.
 *
 * So this is the identity reading. Hue is normalised (nothing else can be),
 * and NOTHING is rounded: `tintToCssColor` of the result must round-trip to
 * the very bytes the stylesheet resolved, or publishing the ground would
 * repaint every shop that has no cover by a shade or two.
 */
export function groundToTint(rgb) {
  const { h, s, l } = rgbToHsl(rgb);
  return { h: ((h % 360) + 360) % 360, s, l };
}

/** HSL(h:0-360, s:0-100, l:0-100) → RGB(0-255). The inverse of `rgbToHsl`,
 * needed because contrast has to be measured on the colour actually
 * painted, not on the HSL triple that describes it. */
export function hslToRgb({ h, s, l }) {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb;
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = ln - c / 2;
  return {
    r: Math.round((rgb[0] + m) * 255),
    g: Math.round((rgb[1] + m) * 255),
    b: Math.round((rgb[2] + m) * 255),
  };
}

/** Accepts `{r,g,b}`, a `#rgb`/`#rrggbb` hex, or an `rgb()`/`rgba()` string —
 * so callers can compare a literal foreground token against a computed tint
 * without converting first. `rgb()` matters because that is the form
 * `tintForeground` hands back for muted text; silently parsing it as black
 * would make every contrast check on it pass for the wrong reason. */
export function parseColor(color) {
  if (color && typeof color === 'object') return color;
  // The strict parser first, so every form it UNDERSTANDS — including
  // `color(srgb …)`, which a computed `color-mix()` serialises as — is read
  // rather than falling through to the black default below. That default is
  // round 1's recorded bug and round 10 hit it again; narrowing what can reach
  // it is the fix, not a new caller remembering to convert.
  const strict = parseColorStrict(color);
  if (strict) return strict;
  const raw = String(color || '').trim();
  const fn = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const [r, g, b] = fn[1].split(/[,\s/]+/).filter(Boolean).map(Number);
    return { r: r || 0, g: g || 0, b: b || 0 };
  }
  const hex = raw.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

/**
 * THE SAME PARSE, BUT IT ADMITS WHEN IT CANNOT.
 *
 * `parseColor` above is deliberately forgiving — it is used to compare a
 * literal token against a computed tint and must never throw mid-measurement.
 * The cost is that it answers BLACK for anything it does not understand, which
 * is round 1's recorded bug: an unparsed value reported every pale page as
 * `rgb(0,0,0)` and every contrast check on it passed for the wrong reason.
 *
 * Anywhere the answer is a DECISION rather than a measurement — "is there a
 * ground here at all, and what is it" — that default is wrong, so this returns
 * `null` instead and the caller has to say what to do about it.
 */
export function parseColorStrict(color) {
  if (color && typeof color === 'object'
    && Number.isFinite(color.r) && Number.isFinite(color.g) && Number.isFinite(color.b)) {
    return { r: color.r, g: color.g, b: color.b };
  }
  const raw = String(color == null ? '' : color).trim();
  // `color(srgb r g b)` — components 0..1. This is not exotic: it is what
  // Chrome SERIALISES a computed `color-mix(in srgb, …)` as, which is exactly
  // the form the page's own fallback ground arrives in. Round 10 hit this and
  // recorded it: "`--shop-tint` computes to `color(srgb 0.956 0.935 0.897)`,
  // which as bytes is `rgb(1,1,1)` and reports every pale page as black."
  const fnSpace = raw.match(/^color\(\s*srgb\s+([^)]+)\)$/i);
  if (fnSpace) {
    const parts = fnSpace[1].split(/[\s/]+/).filter(Boolean).map(Number);
    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
      return {
        r: Math.round(Math.min(1, Math.max(0, parts[0])) * 255),
        g: Math.round(Math.min(1, Math.max(0, parts[1])) * 255),
        b: Math.round(Math.min(1, Math.max(0, parts[2])) * 255),
      };
    }
    return null;
  }
  /* `hsl()` / `hsla()`, THE FORM THIS FILE ITSELF HANDS BACK.
   *
   * `tintToCssColor` writes every derived surface as `hsl(h, s%, l%)` — the
   * glass, the plates, and now the two non-ink surfaces. Until this branch
   * existed, `parseColor` fell through to the hex reader and answered
   * `rgb(0,0,0)` for all of them: the SAME bug the comment above records for
   * `rgb()` in round 1 and for `color(srgb …)` in round 10, a third time, on
   * the file's own output. A contrast check against a surface that measures
   * black passes for the wrong reason, which is worse than not checking. */
  const hslFn = raw.match(/^hsla?\(([^)]+)\)$/i);
  if (hslFn) {
    const parts = hslFn[1].split(/[,\s/]+/).filter(Boolean);
    const nums = parts.slice(0, 3).map((v) => parseFloat(v));
    if (nums.length === 3 && nums.every(Number.isFinite)) {
      return hslToRgb({ h: nums[0], s: nums[1], l: nums[2] });
    }
    return null;
  }
  const fn = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const parts = fn[1].split(/[,\s/]+/).filter(Boolean).map(Number);
    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
      return { r: parts[0], g: parts[1], b: parts[2] };
    }
    return null;
  }
  const hex = raw.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!hex) return null;
  const full = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(color) {
  const { r, g, b } = parseColor(color);
  const ch = (v) => {
    const n = v / 255;
    return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** WCAG contrast ratio between two colours (1–21). */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * CIE L* — perceptual lightness, 0 (black) to 100 (white).
 *
 * This exists because `contrastRatio` cannot answer the question "how
 * visible is this surface against that one". WCAG's ratio is a legibility
 * metric built on LINEAR luminance plus a flare term; it is not
 * perceptually uniform, so the same ratio buys visibly different amounts of
 * separation on a pale page and a dark one. L* is the axis perception
 * actually uses (a unit is roughly a just-noticeable difference for a large
 * field), which makes a FIXED ΔL* mean "the same amount of visible
 * separation" everywhere in the tint band — the property a fixed ratio does
 * not have. Contrast is still measured with `contrastRatio` where the
 * question really is legibility: text on glass, text on the page.
 */
export function lightnessStar(color) {
  const y = relativeLuminance(color);
  return y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y;
}

/** How far apart two colours are on the perceptual lightness axis. */
export function deltaLightnessStar(a, b) {
  return Math.abs(lightnessStar(a) - lightnessStar(b));
}

// Pure white and pure black are not a lazy choice — they are the only pair
// whose 4.5:1 thresholds MEET (both land exactly at a background luminance
// of 0.175). Any softer "near-black" opens a band of mid-tints where
// neither candidate reaches 4.5 and body text is quietly unreadable. The
// reference uses exactly these two for the same reason.
const LIGHT_FG = '#FFFFFF';
const DARK_FG = '#000000';

/** Blend `fg` toward `bg` by `amount` (0–1), in sRGB — how the muted
 * variant is derived, so it is a real colour we can measure rather than an
 * opacity the browser resolves later. */
function mixToward(fg, bg, amount) {
  const a = parseColor(fg);
  const b = parseColor(bg);
  const ch = (x, y) => Math.round(x + (y - x) * amount);
  return { r: ch(a.r, b.r), g: ch(a.g, b.g), b: ch(a.b, b.b) };
}

const rgbToCss = ({ r, g, b }) => `rgb(${r}, ${g}, ${b})`;

/* ── Glass has to SEPARATE from the page, and that is measurable ──────
 *
 * Measured off the reference (shop.app/m/johnelliott, computed styles, both
 * widths):
 *
 *   page ground   rgb(97, 85, 71)
 *   glass fill    rgba(120, 106, 88, 0.85)   → composites to rgb(117,103,85)
 *   glass border  rgb(120, 106, 88)          (the same colour, opaque)
 *
 * The fill is the page colour MOVED, not the page colour repeated — filling
 * with the page's own colour composites back to the page exactly, and every
 * pill, card and circle becomes a hole rather than a surface. That much has
 * not changed.
 *
 * WHAT CHANGED IS THE CURRENCY THE DISTANCE IS COUNTED IN.
 *
 * It used to be a WCAG contrast ratio (1.32:1, the reference's own number,
 * read off its DARK page). A ratio cannot carry this rule across a band that
 * spans light and dark, for two measured reasons:
 *
 *   1. It is not perceptually uniform. Held at 1.32, the painted separation
 *      measures ΔL* 9.6 on the pale nail-salon tint (hsl 13,22%,72%) and
 *      ΔL* 7.8 on a near-black one (hsl 220,6%,26%) — the same "number"
 *      buying a fifth less visible separation at one end than the other.
 *   2. At the light end it is barely reachable in the direction the design
 *      wants at all. On the lightest tint we resolve (l = 94), the lightest
 *      possible surface — white, at GLASS_ALPHA — composites to only
 *      ~1.13:1 above the page. The target was simply unreachable upward, so
 *      "lift the glass" silently became "drop it", by an amount nothing
 *      measured.
 *
 * So the number held is now a PERCEPTUAL distance, CIE ΔL* (`lightnessStar`
 * above), where one unit is about one just-noticeable difference for a large
 * field. Holding it constant holds the amount of *visible* separation
 * constant by construction, at every tint, in whichever direction that tint
 * leaves headroom for.
 *
 * The value: the reference's own glass measures ΔL* 7.0 against its page —
 * ~3 JND, present but the "quiet by design" separation the owner rejected on
 * a pale cover. 14 is twice that (~6 JND) and clearly visible at both ends;
 * it is also comfortably reachable everywhere, since the worst case in the
 * whole band still admits ΔL* 23.4 before the 4.5:1 text rule bites. The cap
 * keeps it glass: with GLASS_ALPHA held at 0.85 the backdrop always keeps
 * contributing, and 14 lands between 1.45:1 and 1.72:1 against the page —
 * a surface, never a flat opaque chip.
 */
export const GLASS_ALPHA = 0.85;
export const GLASS_SURFACE_DELTA_L = 14;
export const GLASS_SURFACE_MAX_DELTA_L = 22;
/** Body text must clear WCAG AA on the glass, not only on the page under it. */
export const GLASS_TEXT_CONTRAST = 4.5;
/* The free-shipping chip does not wear the 0.85 film — it sits on the COVER
 * PHOTO, whose brightness is whatever the owner uploaded, so a film that
 * reads as glass over the page reads as nothing over a bright photo. It gets
 * a near-opaque scrim of the same surface colour instead.
 *
 * WHICH MEANS THE SURFACE SEARCH HAS TO CHECK BOTH COMPOSITES, NOT ONE.
 * A surface picked so that its 0.85 fill holds 4.5:1 is not thereby a surface
 * whose 0.94 scrim does: the scrim sits further from the page and closer to
 * the pure fill, so on a tint whose surface separated DOWNWARD it is darker
 * than the fill that was checked. Swept across the band that gap is real —
 * tints in the upper-mid range measured 4.39:1 on the chip while the pill
 * beside them measured 4.5. One surface, two composites, both checked. */
export const GLASS_SCRIM_ALPHA = 0.94;
/** How far the lit top edge must stand off the fill it sits on, in ΔL*.
 * `inset 0 1px 0` is the entire "raised" cue; a fixed white alpha delivers
 * it on a dark glass and nothing at all on a near-white one. */
export const GLASS_HIGHLIGHT_DELTA_L = 7;
/** And the drop shadow, the other half of the same cue, measured downward
 * against the page it falls on. */
export const GLASS_SHADOW_DELTA_L = 5;
/** The logo plate's lightness. Light enough that a mark authored for white
 * still reads, not white enough to be a sticker cut out of the page. */
export const LOGO_PLATE_LIGHTNESS = 96;

/**
 * A MARK THAT SITS ON THE GLASS, AND SEPARATES BY AREA RATHER THAN BY EDGE.
 *
 * Round 3 gave the mark three concentric steps — a light plate, a
 * page-coloured RING, and a glass rim — and round 15 swept them onto the
 * lockup's pill: plate and rim ALONE floor at ΔL* 0.15 (a pale tint whose
 * glass surface lands on the plate's own lightness kills both at once), while
 * adding the ring floors at 16.43. The ring was the whole guarantee.
 *
 * Round 16 tried to move that guarantee into a single flat plate: one
 * lightness, searched for the value standing 16.43 off the pill. It holds the
 * number and it FAILED THE REPORT, which was measured rather than argued —
 * see `shot/v19-mark-rose-FULL.png`. On a pale tint (rose, page l = 72, pill
 * painted at l ≈ 87) the search cannot go light — 100 is only ΔL* 10.4 away —
 * so it goes DARK, to l = 69. An uploaded mark almost never fills its own
 * circle (the raster carries its own transparent margin), so that plate shows
 * as a ~5px grey annulus around the artwork on a 32px disc. One thick grey
 * ring instead of two thin ones is still "the big border around the logo",
 * asked about three times now.
 *
 * BOTH REQUIREMENTS ARE REAL AND THEY PULL APART. (a) The artwork needs a
 * near-white ground, because it is a raster the owner uploaded and almost
 * certainly drew for one, and we cannot recolour it. (b) The disc's ground has
 * to stand off the pill, or on the tints where the pill lands on 96 the mark
 * dissolves. No single flat lightness satisfies both, which is exactly why
 * round 3 needed a second colour in the first place.
 *
 * SO THE SECOND COLOUR STAYS AND ITS GEOMETRY CHANGES. The plate is a radial
 * ramp, not two concentric fills:
 *
 *     0 → MARK_PLATE_CORE_STOP     `markPlateCore`  — LOGO_PLATE_LIGHTNESS,
 *                                   flat, and it is what the artwork sits on
 *     …  → 100%                     `markPlate`      — the searched rim, ΔL*
 *                                   MARK_PLATE_DELTA_L off the pill
 *
 * A ring has two edges and a constant width, which is what the eye reads as a
 * border. A ramp has neither: its slope is zero at the core stop and it
 * reaches the rim value only at the disc's own boundary, where the disc ends
 * anyway. The floor it carries is the SAME 16.43, measured at the same pair —
 * the rim against the pill — so nothing is given up.
 *
 * AND ON MOST TINTS IT IS NOT THERE AT ALL. The search tries
 * `LOGO_PLATE_LIGHTNESS` first, so wherever 96 already clears the target the
 * rim IS the core, the ramp is flat, and the disc is a plain light plate. The
 * gradient appears only on the pale tints where the pill has crowded the plate
 * — which is the only place anything was ever needed.
 *
 * The GROUND is the pill as PAINTED — the surface colour composited over the
 * page at `GLASS_ALPHA` — not the opaque surface token. Measuring against the
 * opaque colour reports a separation the eye never sees, which is round 2's
 * `color-mix` lesson in a different place.
 */
/* The core is the ONE ground the artwork and the monogram are ever drawn on,
 * so it is round 3's number and it does not move. Stated as its own name
 * rather than reusing `LOGO_PLATE_LIGHTNESS` at the call sites: the classic
 * hero's big plate and this core happen to share a value today and are two
 * different decisions. */
export const MARK_PLATE_CORE_LIGHTNESS = LOGO_PLATE_LIGHTNESS;

/* Where the core stops being flat. Far enough out that an uploaded raster's
 * own margin still lands on flat core — measured on the seeded logo, whose
 * artwork occupies 69% of the disc — and close enough in that the ramp is a
 * feather at the boundary rather than a shaded band. */
export const MARK_PLATE_CORE_STOP = 74;

export const MARK_PLATE_DELTA_L = 16.43;

export function markPlateLightness(tint, surfaceL) {
  const opaque = hslToRgb({ ...tint, l: surfaceL });
  /* TWO GROUNDS, AND THE WORSE OF THEM DECIDES.
   *
   * The same mark is drawn on two surfaces made of the same colour at two
   * alphas: the hero's PILL, which is glass at `GLASS_ALPHA` over the page, and
   * the side panel's SHEET, which is the same surface opaque (a full-height
   * translucent sheet let the product photographs read straight through it, and
   * the reference's own sheet is opaque). Those two composite to different
   * lightnesses, so a plate measured against one can sit closer to the other —
   * measured live, targeting the pill alone left the panel's mark at ΔL* 13.95
   * in CLASSIC. The search therefore has to clear the target on BOTH, which is
   * the only version of "the plate separates" that is true wherever the mark
   * is actually drawn. */
  const painted = composite(opaque, hslToRgb(tint), GLASS_ALPHA);
  const hue = { h: tint.h, s: Math.min(tint.s, 8) };
  const step = (l) => Math.min(
    deltaLightnessStar(hslToRgb({ ...hue, l }), painted),
    deltaLightnessStar(hslToRgb({ ...hue, l }), opaque),
  );
  if (step(LOGO_PLATE_LIGHTNESS) >= MARK_PLATE_DELTA_L) return LOGO_PLATE_LIGHTNESS;
  for (let l = LOGO_PLATE_LIGHTNESS + 0.5; l <= 100; l += 0.5) {
    if (step(l) >= MARK_PLATE_DELTA_L) return l;
  }
  for (let l = LOGO_PLATE_LIGHTNESS - 0.5; l >= 0; l -= 0.5) {
    if (step(l) >= MARK_PLATE_DELTA_L) return l;
  }
  // Unreachable across the swept band, but a search that can return nothing is
  // a search that silently returns `undefined` the day the band moves.
  let best = { l: LOGO_PLATE_LIGHTNESS, d: step(LOGO_PLATE_LIGHTNESS) };
  for (let l = 0; l <= 100; l += 0.5) {
    const d = step(l);
    if (d > best.d) best = { l, d };
  }
  return best.l;
}

/* ── Muted text has to survive the surfaces, not only the page ────────
 *
 * `--shop-text-muted` used to be walked down from body text toward the tint
 * for as long as it held 3:1 AGAINST THE TINT — and then painted on the
 * glass, the soft hover plate and the cards as well. On a pale ground the
 * glass separates DOWNWARD (there is no headroom above a near-white page),
 * so the muted colour lands on a surface it was never checked against.
 * Measured live on the product page's review card:
 *
 *     FULL (bronze cover)  5.05:1      FULL (dark cover)  5.27:1
 *     LIGHT                2.28:1      CLASSIC            2.32:1
 *
 * — under 3:1 in the two modes an owner is most likely to pick, on bylines
 * and item names, everywhere in the storefront and not only on that card.
 * This is the same shape of bug `glassSurfaceLightness` already carries a
 * fix for one layer up ("one surface, two composites, both checked"): a
 * colour checked against ONE of the grounds it is painted on is not checked.
 *
 * So muted is now derived against EVERY ground the page publishes —
 * `tintForeground` returns them as `mutedGrounds`, and a guard proves that
 * set equals the background tokens `hooks/useTintVars.js` actually writes,
 * in both directions.
 *
 * TWO NUMBERS, PULLING OPPOSITE WAYS, WHICH IS WHY NEITHER IS A RATIO ALONE.
 *
 * The target is WCAG AA for small text, because that is what muted text is:
 * a 12px byline, a strikethrough compare-at price, a category caption. But
 * "muted" is a PERCEPTUAL property — it has to look quieter than body text —
 * and a pure contrast target would happily answer "body text" and call it
 * readable. So the search is bounded from both ends: it starts at the first
 * mix that stands `MUTED_MIN_DELTA_L` off the body colour on the CIE L* axis
 * (the same currency, and the same value, `GLASS_HIGHLIGHT_DELTA_L` uses for
 * "one clearly visible step"), and walks away from the text for as long as
 * every published ground still holds the ratio.
 *
 * AND IT DEGRADES HONESTLY. Muted is body text moved toward the page, so it
 * can never beat body text on the same ground; at a handful of mid tints
 * body text itself cannot hold 4.5:1 on every surface, and there the search
 * keeps its most-contrast candidate rather than pretending. Swept over the
 * whole band the floor never falls below 3.6:1 — the design system's old
 * muted floor was 3 — while the three real modes measure 4.6:1 or better on
 * every ground. Round 3's finding, one layer down: no single ratio is
 * reachable at both ends, so the rule states the target and the fallback. */
export const MUTED_TEXT_CONTRAST = 4.5;
export const MUTED_MIN_DELTA_L = 7;

/* `--shop-soft-bg` is the other half of this fix, and it was wrong in
 * exactly the way muted was: a hardcoded `l + 8` on a dark tint and `l - 5`
 * on a pale one, i.e. ALWAYS A STEP TOWARD THE TEXT, never measured against
 * anything. It is a real ground — the search field's plate, the search row
 * and sort row hover, the Buy-Again card, the About page's social discs —
 * and muted text sits on all of those, at 2.32–2.97:1 in all four pages
 * above before this.
 *
 * Derived now, by the same rule the glass uses: a measured ΔL* off the page,
 * taken AWAY from the foreground where the page leaves room, and toward it
 * (by the same, small, amount) only where it does not — a CLASSIC page is
 * pure white and there is nothing above it. Small on purpose: this is a
 * hover cue, not a second surface, and the glass already owns ΔL* 14. */
export const SOFT_SURFACE_DELTA_L = 4;

/* ── INK IS NOT A SURFACE ────────────────────────────────────────────
 *
 * `tintForeground` returns `#000000` or `#ffffff` for `--shop-text`, and that
 * pair is deliberate: they are the only two whose 4.5:1 thresholds meet, so
 * body copy is legible across the whole tint band. It is the right answer for
 * a GLYPH and a wrong answer for everything else, and the page had been using
 * it for four other jobs — a 144×42 selected pill, a full-bleed announcement
 * band, a 1px button ring, a 2px focus outline. Every one of them painted raw
 * black on a page whose entire premise is that its colours come from the
 * owner's photograph. The owner reported three of the four separately.
 *
 * So there are two surfaces here, and the difference between them is what the
 * surface has to DO.
 *
 * ── `--shop-contrast`: read BEFORE the page ─────────────────────────────
 *
 * An announcement band, a primary Apply button, a filled progress rail, a
 * focus ring. These are not "on the page" in the way a pill is; they interrupt
 * it. The reference does the same thing and hardcodes it — dk-store's own bar
 * is `rgb(29,29,29)` under white type on a `rgb(248,248,248)` page, measured
 * first-hand — so the target is that colour's RELATIONSHIP, not its value: the
 * page's own hue and saturation, driven to the far end of a surface band.
 *
 * THE BAND IS WIDER THAN THE TINT'S, AND THAT IS THE WHOLE POINT.
 * `TINT_MIN/MAX_LIGHTNESS` (26/94) bound what a PAGE may be — a ground has to
 * carry body copy at both ends. A band that interrupts the page carries one
 * short line and states its own foreground, so it may go further. It may not
 * go all the way: `l = 0` is `#000000`, which is the bug this exists to fix.
 * How much further is `SURFACE_MIN_DELTA_L_FROM_INK`, below.
 *
 * ── `--shop-selected`: "this control is on" ─────────────────────────────
 *
 * A chosen category pill, an applied filter chip, a lit quick-filter. The
 * owner: keep it reading as selected, but as a glass-selected surface
 * consistent with the other pills rather than the one opaque chip on a tinted
 * page. So it is the SAME material — the page colour moved, filmed at
 * `GLASS_ALPHA` over a blur, edged by that colour opaque — taken one more
 * measured step out. `GLASS_SURFACE_DELTA_L` is 14; this is 30, i.e. a second
 * step of the same size and a bit, which is far enough that a chosen pill
 * reads as chosen from across the row and near enough that it is still
 * obviously the same family.
 */

/** How far a chosen control's PAINTED surface stands off the page, in CIE ΔL*.
 *  The rest-state glass holds `GLASS_SURFACE_DELTA_L` (14); a chosen one is a
 *  second step out. Above ~36 the pill stops reading as glass at all. */
export const SELECTED_SURFACE_DELTA_L = 30;
export const SELECTED_SURFACE_MAX_DELTA_L = 44;

/* THE BAND EVERY SURFACE LIVES IN, STATED AS A DISTANCE FROM THE INK.
 *
 * The first draft of this wrote the band as two HSL lightnesses (16 and 97)
 * and a solved worst-case ratio. That was right about the dark end and wrong
 * about the light one: on a shop with a dark cover the ink is `#ffffff`, and a
 * band at HSL 97 measures CIE L* 97.2 — 2.8 off the ink, i.e. the same defect
 * as `#000000` on a light page, mirrored. A guard caught it, which is the
 * whole reason it measures distance-from-ink rather than checking a name.
 *
 * So the band is ONE number: a surface must stand at least one clearly visible
 * step off either ink, on the CIE L* axis. That step is
 * `GLASS_HIGHLIGHT_DELTA_L` — the same value this file already calls "one
 * clearly visible step" for the lit edge and for muted text — rather than a
 * fourth number meaning the same thing.
 *
 * Everything else falls out of it, and is asserted rather than assumed:
 *
 *  • the darkest surface is L* 7 and the lightest L* 93, so neither can ever
 *    be `#000000` or `#ffffff`, which is the entire difference between a
 *    surface and the ink drawn on it;
 *  • the worst page for a band is the one where the two ends tie —
 *    (Yp + .05)² = (Yhi + .05)(Ylo + .05) — and with these ends that page
 *    still gets 3.90:1, clearing WCAG's 3:1 non-text floor, which matters
 *    because the focus ring reads off this same token.
 *
 * For reference, the bands that do not work: 26–94 (the TINT band, which a
 * page must live in because a page carries body copy) ties at 2.95:1, under
 * the floor; 0–100 is the raw ink and is the bug.
 */
export const SURFACE_MIN_DELTA_L_FROM_INK = GLASS_HIGHLIGHT_DELTA_L;
/** The floor the band above delivers: WCAG's non-text contrast minimum. */
export const CONTRAST_SURFACE_MIN_RATIO = 3;

export const CONTRAST_SURFACE_SATURATION = 10;

/** sRGB blend of `top` over `bottom` at `alpha`. */
function composite(top, bottom, alpha) {
  const ch = (a, b) => a * alpha + b * (1 - alpha);
  return { r: ch(top.r, bottom.r), g: ch(top.g, bottom.g), b: ch(top.b, bottom.b) };
}

/**
 * The lightness a glass surface must take so that, filled at `GLASS_ALPHA`
 * over `tint`, it stands `GLASS_SURFACE_DELTA_L` off the page perceptually
 * AND still carries `text` at 4.5:1.
 *
 * Both constraints matter and they pull opposite ways on a dark tint: the
 * reference lifts its glass toward the light, but a tint that only just
 * earned white text has no headroom to spend — lifting the surface there
 * would quietly drop body text under AA on every pill and every card. So
 * lighter is only PREFERRED, never assumed; when lifting would cost
 * legibility the surface goes the other way and separates downward instead.
 * That is why this is a search over the whole range rather than `l + 8`.
 */
export function glassSurfaceLightness(tint, text) {
  const ground = hslToRgb(tint);
  const step = 0.5;
  let best = null;
  for (let l = 0; l <= 100; l += step) {
    const fill = hslToRgb({ ...tint, l });
    const seen = composite(fill, ground, GLASS_ALPHA);
    if (contrastRatio(text, seen) < GLASS_TEXT_CONTRAST) continue;
    // The chip's near-opaque scrim is the same colour at a different alpha,
    // so it is the same choice — and it has to clear AA too.
    if (contrastRatio(text, composite(fill, ground, GLASS_SCRIM_ALPHA)) < GLASS_TEXT_CONTRAST) continue;
    const separation = deltaLightnessStar(seen, ground);
    if (separation > GLASS_SURFACE_MAX_DELTA_L) continue;
    const lifted = l >= tint.l;
    const reaches = separation >= GLASS_SURFACE_DELTA_L;
    const candidate = { l, separation, lifted, reaches, miss: Math.abs(separation - GLASS_SURFACE_DELTA_L) };
    if (!best) { best = candidate; continue; }
    // A surface that reaches the target beats one that does not; among those
    // that reach it, prefer a LIFTED one (the reference's direction) and then
    // the one closest to the target, so the lift stays a lift, not a slab.
    if (candidate.reaches !== best.reaches) { if (candidate.reaches) best = candidate; continue; }
    if (candidate.reaches) {
      if (candidate.lifted !== best.lifted) { if (candidate.lifted) best = candidate; continue; }
      if (candidate.miss < best.miss) best = candidate;
    } else if (candidate.separation > best.separation) best = candidate;
  }
  return best ? best.l : tint.l;
}

/**
 * The lightness `--shop-contrast` takes: the far end of the contrast band,
 * whichever end that is.
 *
 * Not "the ink's end". On a mid-lightness page the ink's own end is the NEARER
 * one — a page at L* 55 is closer to black than to white, so a black band on it
 * is quieter than a white one, and the band exists to be loud. So both ends are
 * measured against the page and the further one wins. That is also why this
 * returns the same answer for a dark page and its inverse rather than
 * switching on `isDarkTint`: it is a measurement, not a branch.
 */
/**
 * Is this colour a SURFACE — far enough from both inks that it can never be
 * read as the ink itself? One clearly visible CIE step from black and from
 * white, which is the whole of the band rule; see
 * `SURFACE_MIN_DELTA_L_FROM_INK`.
 */
export function isSurfaceColour(rgb) {
  const l = lightnessStar(rgb);
  return l >= SURFACE_MIN_DELTA_L_FROM_INK && l <= 100 - SURFACE_MIN_DELTA_L_FROM_INK;
}

/** The HSL lightnesses at which `tint`'s own hue enters and leaves the surface
 *  band. Searched rather than tabulated: the same HSL lightness lands at a
 *  different CIE L* for a saturated hue than for a neutral one, so a pair of
 *  constants would be right for grey and wrong for everything else. */
function surfaceBandFor(tint) {
  let lo = null;
  let hi = null;
  for (let l = 0; l <= 100; l += 0.5) {
    if (!isSurfaceColour(hslToRgb({ ...tint, l }))) continue;
    if (lo === null) lo = l;
    hi = l;
  }
  return { lo: lo === null ? tint.l : lo, hi: hi === null ? tint.l : hi };
}

export function contrastSurfaceFor(tint) {
  /* THE SATURATION IS REDUCED FIRST, AND THE BAND IS FOUND ON THE RESULT.
   *
   * A hue's CIE lightness at a given HSL lightness depends on its SATURATION,
   * so a band measured on the page's own saturation is the wrong band for a
   * surface that will be painted at a lower one. Measured on the forest shop
   * it was wrong by 0.2 of a step — enough to put the band 6.80 off the ink
   * where the rule says 7, which a guard caught. Derive once, from the colour
   * that is actually painted. */
  const base = { h: tint.h, s: Math.min(tint.s, CONTRAST_SURFACE_SATURATION) };
  const { lo, hi } = surfaceBandFor(base);
  const page = hslToRgb(tint);
  let best = null;
  for (const l of [lo, hi]) {
    const ratio = contrastRatio(hslToRgb({ ...base, l }), page);
    if (!best || ratio > best.ratio) best = { l, ratio };
  }
  return { ...base, l: best.l };
}

/** The lightness half of `contrastSurfaceFor`, for callers that only need it. */
export function contrastSurfaceLightness(tint) {
  return contrastSurfaceFor(tint).l;
}

/**
 * The lightness a CHOSEN control's surface must take so that, filled at
 * `GLASS_ALPHA` over `tint`, it stands `SELECTED_SURFACE_DELTA_L` off the page
 * — one measured step further out than the rest-state glass — while still
 * carrying a legible foreground of its own.
 *
 * Written as a search for the same reason `glassSurfaceLightness` is: on a
 * page near either end of the band there is no room on one side, and a
 * `l ± 30` would silently clamp to the axis end, which is the raw-ink bug
 * arriving by a different route. Lighter is PREFERRED (the reference's
 * direction) and never assumed; where the target cannot be reached in either
 * direction the search keeps its best separation rather than pretending.
 *
 * It is deliberately NOT required to move in the same direction as the glass.
 * On a mid tint the glass may have separated downward for contrast headroom it
 * needed at ΔL* 14 and still have plenty of room upward at 30; forcing the
 * chosen state to follow it would make the loud surface the quiet one.
 */
export function selectedSurfaceLightness(tint) {
  const ground = hslToRgb(tint);
  let best = null;
  /* THE SEARCH RANGE IS THE SURFACE BAND, NOT THE WHOLE AXIS. A pill's border
   * is this colour OPAQUE, so a search allowed to answer `l = 100` puts a pure
   * white ring on a chosen pill — the same defect as the black fill, at the
   * other end. Bounded by `surfaceBandFor`, the answer is always a colour and
   * never an end. */
  const band = surfaceBandFor(tint);
  for (let l = band.lo; l <= band.hi; l += 0.5) {
    const fill = hslToRgb({ ...tint, l });
    const painted = composite(fill, ground, GLASS_ALPHA);
    // Its own ink, measured on the surface AS PAINTED — the same rule the
    // glass holds, applied to the ground this surface actually is.
    const ink = contrastRatio(LIGHT_FG, painted) >= contrastRatio(DARK_FG, painted)
      ? LIGHT_FG : DARK_FG;
    if (contrastRatio(ink, painted) < GLASS_TEXT_CONTRAST) continue;
    const separation = deltaLightnessStar(painted, ground);
    if (separation > SELECTED_SURFACE_MAX_DELTA_L) continue;
    const reaches = separation >= SELECTED_SURFACE_DELTA_L;
    const candidate = {
      l, separation, reaches, lifted: l >= tint.l,
      miss: Math.abs(separation - SELECTED_SURFACE_DELTA_L),
    };
    if (!best) { best = candidate; continue; }
    if (candidate.reaches !== best.reaches) { if (candidate.reaches) best = candidate; continue; }
    if (candidate.reaches) {
      if (candidate.lifted !== best.lifted) { if (candidate.lifted) best = candidate; continue; }
      if (candidate.miss < best.miss) best = candidate;
    } else if (candidate.separation > best.separation) best = candidate;
  }
  return best ? best.l : tint.l;
}

/**
 * The alpha a white lit edge needs so it actually reads as lit ON THIS fill.
 *
 * The two hand-picked alphas this replaces — `rgba(255,255,255,0.55)` on a
 * light tint, `0.22` on a dark one — measure ΔL* 5.5 and 13.1 against the
 * fills they sit on. Not nothing at the pale end, but less than half the cue
 * the dark end gets: the same asymmetry the fill had, and in the same
 * direction, because a near-white glass has very little room left above it
 * however much white you pour on. Solved by measurement instead: the
 * smallest alpha that lifts the fill by `GLASS_HIGHLIGHT_DELTA_L`, capped so
 * it can never become a white bar.
 */
function litEdgeAlpha(fill) {
  const white = { r: 255, g: 255, b: 255 };
  const base = lightnessStar(fill);
  for (let a = 0.08; a <= 0.9; a += 0.01) {
    if (lightnessStar(composite(white, fill, a)) - base >= GLASS_HIGHLIGHT_DELTA_L) {
      return Math.round(a * 100) / 100;
    }
  }
  return 0.9;
}

/**
 * And the shadow underneath it, by the same measurement against the page.
 * A fixed `rgba(0,0,0,0.12)` reads on a pale page and disappears on a dark
 * one, where there is very little left to darken — so the alpha is searched
 * and simply saturates at the cap when the page is already near-black.
 */
function shadowAlpha(ground) {
  const black = { r: 0, g: 0, b: 0 };
  const base = lightnessStar(ground);
  for (let a = 0.06; a <= 0.6; a += 0.01) {
    if (base - lightnessStar(composite(black, ground, a)) >= GLASS_SHADOW_DELTA_L) {
      return Math.round(a * 100) / 100;
    }
  }
  return 0.6;
}

/**
 * The lightness `--shop-soft-bg` takes: `SOFT_SURFACE_DELTA_L` off the page,
 * in the direction that moves AWAY from `text` when the page leaves room
 * there, and toward it otherwise. See `SOFT_SURFACE_DELTA_L`.
 */
export function softSurfaceLightness(tint, text) {
  const base = lightnessStar(hslToRgb(tint));
  // White text ⇒ step darker; black text ⇒ step lighter.
  const away = relativeLuminance(text) > 0.5 ? -1 : 1;
  for (const dir of [away, -away]) {
    for (let l = tint.l; l >= 0 && l <= 100; l += dir * 0.5) {
      if (Math.abs(lightnessStar(hslToRgb({ ...tint, l })) - base) >= SOFT_SURFACE_DELTA_L) return l;
    }
  }
  return clamp(tint.l + away * SOFT_SURFACE_DELTA_L, 0, 100);
}

/**
 * The foreground palette for a given page tint: body text, muted text, and
 * the glass surface tokens layered on it. Chosen by MEASURED contrast
 * against the tint, never hardcoded — the whole point is that a pale cover
 * and a near-black cover both stay readable.
 *
 * Muted text is stepped down from body text toward the tint only as far as
 * it can go while holding 3:1, so "secondary" still reads as secondary
 * without becoming decoration.
 */
export function tintForeground(tint) {
  const bg = hslToRgb(tint);
  const useLight = contrastRatio(LIGHT_FG, bg) >= contrastRatio(DARK_FG, bg);
  const text = useLight ? LIGHT_FG : DARK_FG;

  const surfaceL = glassSurfaceLightness(tint, text);
  const surface = { ...tint, l: surfaceL };
  const surfaceRgb = hslToRgb(surface);
  // One step further from the page again, in the same direction the surface
  // took, so hover reads as "lifted more" on a dark tint and a pale one alike.
  const hoverL = clamp(surfaceL + (surfaceL >= tint.l ? 4 : -4), 0, 100);
  const hoverRgb = hslToRgb({ ...tint, l: hoverL });
  const soft = { ...tint, l: softSurfaceLightness(tint, text) };
  const softRgb = hslToRgb(soft);

  /* EVERY GROUND THE PAGE PAINTS, KEYED BY THE TOKEN THAT PAINTS IT.
   *
   * Muted text is checked against all of them, not against the page alone —
   * see `MUTED_TEXT_CONTRAST`. Keyed by token name rather than collected in
   * an array so the guard can compare this set against the tokens
   * `useTintVars` publishes and fail in BOTH directions: a new surface token
   * that muted has not learned about, and a ground listed here that nothing
   * paints. `--shop-logo-plate` is deliberately absent — it declares its own
   * foreground (`--shop-logo-plate-text`), so muted is never on it.
   *
   * `--shop-scrim-bg` is composited over the page here although it is really
   * painted over the cover PHOTO; it lies between the fill and the opaque
   * surface, both of which are checked, so the approximation cannot hide a
   * failure it would otherwise catch. */
  const mutedGrounds = {
    '--shop-tint': bg,
    '--shop-page-bg': bg,
    '--shop-glass-bg': composite(surfaceRgb, bg, GLASS_ALPHA),
    '--shop-card-bg': composite(surfaceRgb, bg, GLASS_ALPHA),
    '--shop-glass-bg-hover': composite(hoverRgb, bg, GLASS_ALPHA),
    '--shop-scrim-bg': composite(surfaceRgb, bg, GLASS_SCRIM_ALPHA),
    '--shop-glass-solid': surfaceRgb,
    '--shop-glass-border': surfaceRgb,
    '--shop-border': surfaceRgb,
    '--shop-soft-bg': softRgb,
  };
  const worstOnAnyGround = (colour) => Object.values(mutedGrounds)
    .reduce((lowest, ground) => Math.min(lowest, contrastRatio(colour, ground)), Infinity);

  /* Bounded from both ends: quiet enough to READ as muted, contrasty enough
   * to be read at all, on every one of those grounds. `quietest` is the
   * first candidate that is visibly muted and is the answer only when even
   * that one cannot hold the ratio — an honest degradation, not a silent
   * one; see `MUTED_TEXT_CONTRAST`. */
  const textL = lightnessStar(text);
  let quietest = null;
  let muted = null;
  for (let amount = 0.02; amount <= 0.9; amount += 0.02) {
    const candidate = mixToward(text, bg, amount);
    if (Math.abs(lightnessStar(candidate) - textL) < MUTED_MIN_DELTA_L) continue;
    if (!quietest) quietest = candidate;
    if (worstOnAnyGround(candidate) < MUTED_TEXT_CONTRAST) break;
    muted = candidate;
  }
  muted = muted || quietest || parseColor(text);

  /* The two non-ink surfaces, solved here so `useTintVars` publishes exactly
   * what was measured and nothing re-derives them a second way. */
  const contrastSurface = contrastSurfaceFor(tint);
  const selected = { ...tint, l: selectedSurfaceLightness(tint) };
  const selectedPainted = composite(hslToRgb(selected), bg, GLASS_ALPHA);

  return {
    isDarkTint: useLight,
    text,
    textMuted: rgbToCss(muted),
    mutedGrounds,
    surfaceLightness: surfaceL,
    softBg: tintToCssColor(soft, 1),
    // The measured shop.app recipe: the page colour LIFTED (or, when a dark
    // tint has no contrast headroom to spend, dropped) far enough to clear
    // 1.32:1 against the page, at 0.85 over a blur, edged by that same
    // colour opaque — which lands at 1.38:1, exactly the reference.
    glassBg: tintToCssColor(surface, GLASS_ALPHA),
    glassBgHover: tintToCssColor({ ...tint, l: hoverL }, GLASS_ALPHA),
    glassBorder: tintToCssColor(surface, 1),
    glassSolid: tintToCssColor(surface, 1),
    // The free-shipping chip sits on the COVER PHOTO, whose brightness is
    // whatever the owner uploaded — a shared 0.85 film that reads as glass
    // over the page reads as nothing over a bright photo. It gets a near
    // opaque scrim of the same surface colour instead, so its text has the
    // one background it was contrast-checked against.
    scrimBg: tintToCssColor(surface, GLASS_SCRIM_ALPHA),
    // Both derived, not picked: see `litEdgeAlpha` / `shadowAlpha`.
    glassHighlight: `rgba(255, 255, 255, ${litEdgeAlpha(hslToRgb(surface))})`,
    glassShadow: `rgba(0, 0, 0, ${shadowAlpha(bg)})`,
    /* THE TWO SURFACES THAT ARE NOT THE INK. See the block comment above
     * `SELECTED_SURFACE_DELTA_L`. Both carry the page's own hue and muted
     * saturation, both declare their own foreground, and neither can ever be
     * `#000000` or `#ffffff` — which is the entire difference between a
     * surface and the ink drawn on it.
     *
     * The band's saturation is capped harder than the page's: at lightness 16
     * or 97 the tint's full 22% reads as a colour cast rather than as a tinted
     * neutral, and this surface's job is to be read, not to be admired. */
    contrastBg: tintToCssColor(contrastSurface, 1),
    contrastInk: contrastRatio(DARK_FG, hslToRgb(contrastSurface)) >= 4.5 ? DARK_FG : LIGHT_FG,
    selectedSolid: tintToCssColor(selected, 1),
    selectedInk: contrastRatio(DARK_FG, selectedPainted) >= contrastRatio(LIGHT_FG, selectedPainted)
      ? DARK_FG : LIGHT_FG,
    /* THE LOGO PLATE IS NOT A GLASS SURFACE, AND THAT IS DELIBERATE.
     *
     * Every other surface on this page is ours to colour. The logo is not:
     * it is a raster the owner uploaded, and it was almost certainly drawn
     * for a light ground — a navy wordmark, a dark monogram. Sitting it on
     * the glass colour means the plate follows the tint, so on a dark cover
     * the mark turns into a smudge on a smudge, and on a pale one the plate
     * and the page are close enough that the whole thing reads as a
     * washed-out ring rather than a brand mark.
     *
     * So the plate is always light, and it borrows the tint's hue at low
     * saturation so it still belongs to this page rather than being a white
     * sticker on it. Separation from the page is then the ring's job (the
     * page colour) plus the rim (the glass colour), which is a measured
     * step on any tint — including the pale one, where a light plate on a
     * light page needs the ring to say where it ends. */
    logoPlate: tintToCssColor({ h: tint.h, s: Math.min(tint.s, 8), l: LOGO_PLATE_LIGHTNESS }, 1),
    logoPlateText: contrastRatio(DARK_FG, hslToRgb({ h: tint.h, s: Math.min(tint.s, 8), l: LOGO_PLATE_LIGHTNESS })) >= 4.5
      ? DARK_FG
      : LIGHT_FG,
    /* AND THE PLATE FOR A MARK DRAWN ON THE GLASS, WHICH IS A DIFFERENT GROUND.
     *
     * `--shop-logo-plate` above is for the classic hero's big mark, which sits
     * on the page (or on the cover photo) and keeps its own hairline. The
     * lockup's disc and the side panel's mark sit on a GLASS PILL, and glass is
     * a measured ΔL* away from the page — so a plate checked against the page
     * says nothing about whether the disc is visible where it actually is.
     *
     * TWO VALUES, ONE DISC. `markPlate` is the RIM — searched against the pill,
     * and the pair the separation floor is measured on. `markPlateCore` is the
     * flat centre the artwork sits on, which never moves. Where the search can
     * stay at the core's own lightness the two are equal and the disc is a
     * plain light plate; where it cannot, the difference is spent as a ramp
     * rather than as a ring. See `markPlateLightness`. */
    markPlate: tintToCssColor({ h: tint.h, s: Math.min(tint.s, 8), l: markPlateLightness(tint, surfaceL) }, 1),
    /* The core — flat across the artwork's own footprint, and the ground the
     * monogram's contrast is measured on, because the monogram is centred and
     * never reaches the ramp. */
    markPlateCore: tintToCssColor({ h: tint.h, s: Math.min(tint.s, 8), l: MARK_PLATE_CORE_LIGHTNESS }, 1),
    markPlateText: contrastRatio(
      DARK_FG,
      hslToRgb({ h: tint.h, s: Math.min(tint.s, 8), l: MARK_PLATE_CORE_LIGHTNESS }),
    ) >= 4.5 ? DARK_FG : LIGHT_FG,
    /* THE BRAND STRIP'S MAT — and the answer to "why isn't the logo pic a
     * circle?".
     *
     * It never was. The mark was letterboxed by `object-fit: contain` inside a
     * circular plate, so a wide wordmark sat as a small rectangle in the
     * middle of a disc it could never touch; the roundness belonged to the
     * plate, never to the artwork. The strip shows the LOGOS as the items now
     * (backlog item 1, and the reference does exactly this — dk-store's
     * `.dk-brand-group` children are bare `<img>`, `border-radius: 0px`,
     * `object-fit: contain`, transparent ground), so the plate is gone and
     * with it the question.
     *
     * What cannot go is the reason a plate was there: the mark is a raster the
     * owner uploaded, usually a dark-ink transparent PNG drawn for a light
     * ground, and this page's ground can be a dark cover. So the mat is a
     * MEASUREMENT rather than a constant — `transparent` wherever the page is
     * light enough to carry the artwork itself, which is every page whose own
     * text resolves dark, and the light plate exactly where it is not. On the
     * pages the owner is looking at (bronze, forest, near-white) it resolves
     * to `transparent` and the logos are bare, as asked.
     *
     * It owns its own foreground because the no-logo fallback prints the
     * brand's INITIAL on it, and that letter is the one thing on the strip
     * whose ground is not the page. */
    logoMat: useLight
      ? tintToCssColor({ h: tint.h, s: Math.min(tint.s, 8), l: LOGO_PLATE_LIGHTNESS }, 1)
      : 'transparent',
    logoMatText: useLight
      ? (contrastRatio(DARK_FG, hslToRgb({ h: tint.h, s: Math.min(tint.s, 8), l: LOGO_PLATE_LIGHTNESS })) >= 4.5
        ? DARK_FG : LIGHT_FG)
      : text,
  };
}

export function tintToCssColor({ h, s, l }, alpha = 1) {
  return alpha >= 1
    ? `hsl(${h}, ${s}%, ${l}%)`
    : `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
}

/**
 * Draws `imgEl` to a tiny offscreen canvas and derives a tint from it.
 * Returns `null` — never throws — when the canvas is tainted (cross-origin
 * image without CORS headers), when 2D context creation fails, or when the
 * image has no readable pixels. Callers fall back to the theme colour.
 */
export function deriveCoverTint(imgEl, { sampleSize = 8, createCanvas } = {}) {
  if (!imgEl) return null;
  try {
    const canvas = createCanvas ? createCanvas() : document.createElement('canvas');
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(imgEl, 0, 0, sampleSize, sampleSize);
    // Throws SecurityError on a tainted (cross-origin, no-CORS) canvas.
    const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const avg = averageRgb(data);
    return rgbToTint(avg);
  } catch (_err) {
    return null;
  }
}

/* ── The owner's knob: how much of this page is the cover's colour ────
 *
 * "I want to allow the biz owner to select the classic design which doesn't
 * do the whole background-colouring thing — or make it, but much much
 * lighter, close to white as possible."
 *
 * Three modes, and — this is the whole design — a mode is a TRANSFORM ON THE
 * TINT, not a second rendering path. Everything downstream (`tintForeground`,
 * `glassSurfaceLightness`, `litEdgeAlpha`, `shadowAlpha`, every `--shop-*`
 * token, every surface that composes `glass.module.css`) runs unchanged and
 * keeps measuring. That matters more here than anywhere else: LIGHT and
 * CLASSIC are exactly the pale end of the band where a FIXED WCAG ratio was
 * measured to collapse — on the lightest tint we resolve, white at
 * GLASS_ALPHA reaches only ~1.13:1 above the page, so the old rule was
 * unreachable upward and silently inverted. The ΔL* rule that replaced it is
 * reachable in both directions everywhere, which is precisely what lets these
 * two modes exist without their own special-cased glass.
 */
export const PAGE_BG_MODES = ['FULL', 'LIGHT', 'CLASSIC'];
export const DEFAULT_PAGE_BG_MODE = 'FULL';

/** Narrow whatever the API sent (including nothing at all, from a server that
 * predates the setting) to a mode. Unknown → FULL, because FULL is what every
 * existing shop renders today and a new setting must not repaint them. */
export function resolvePageBackgroundMode(value) {
  return typeof value === 'string' && PAGE_BG_MODES.includes(value)
    ? value
    : DEFAULT_PAGE_BG_MODE;
}

/* LIGHT keeps the cover's HUE — the shop still belongs to its own photo —
 * and throws away almost everything else. Saturation is capped far below the
 * full band so the colour is a hint rather than a wash, and lightness is
 * PINNED rather than clamped: "close to white as possible" has to be true of
 * a near-black cover too, and a clamp would leave a dark store dark. */
export const LIGHT_MAX_SATURATION = 10;
export const LIGHT_LIGHTNESS = 96;

/* CLASSIC is the page from before any of this landed, read off the commit
 * that introduced the tint (`f2291b830^`), not invented:
 *
 *   --shop-page-bg: #ffffff     --shop-soft-bg: #F7F7F7
 *   --shop-border:  #ECEEF3     --shop-text:    #0B0F34
 *
 * `#ECEEF3` is hsl(223, 23%, 94%) — the classic palette is a cool blue-grey
 * family against navy text, not a neutral grey, so the classic ground takes
 * its hue and saturation from that border token and its LIGHTNESS from the
 * page (white). Rendering it as an HSL rather than as a fixed hex is what
 * lets the same measured machinery run on it: the surfaces come out in the
 * classic cast, and they come out at the separation the design guarantees.
 *
 * That last point is a deliberate departure and worth naming. The classic
 * page separated its surfaces with a hairline at ~ΔL* 12 and white cards at
 * ΔL* 0 — the second of which is the invisible-glass bug this codebase has
 * already fixed once. CLASSIC restores the classic GROUND and the classic
 * PALETTE; it does not restore the classic failure. */
export const CLASSIC_PAGE_HEX = '#FFFFFF';
export const CLASSIC_SOFT_HEX = '#F7F7F7';
export const CLASSIC_BORDER_HEX = '#ECEEF3';
export const CLASSIC_TINT = { h: 223, s: 23, l: 100 };

/**
 * CLASSIC, IN THE BUSINESS'S OWN COLOUR.
 *
 * The constant above is a FALLBACK now, not the answer. The owner, on seeing
 * CLASSIC rendered: "the white background is the original background and using
 * the theme color where possible". `h 223` is `#ECEEF3`'s hue — the old border
 * token — so every surface on a classic page came out cool blue-grey no matter
 * whose shop it was. On dk-store, whose accent is `#F36523`, the pill row
 * measured `rgba(202,207,222,.85)`: grey furniture in an orange shop, which is
 * the complaint this codebase has now answered three times in three places.
 *
 * So the LIGHTNESS stays pinned at white — that is what CLASSIC means and the
 * owner said so plainly — and the hue and saturation come from the salon's own
 * theme accent. Saturation is reduced the same way a cover-derived tint is,
 * because a surface solved ΔL* 14 below a fully-saturated ground is a strong
 * colour, and CLASSIC is meant to be quiet.
 *
 * Note the name collision this sits inside, because it has already cost
 * rounds: `webStoreTheme: 'classic'` is a THEME that remaps orange to black;
 * `heroStyle: 'classic'` is the old HERO; `shopBackgroundMode: 'CLASSIC'` is
 * this. Three unrelated settings, one word. This function is about the third
 * and reads the first.
 */
export function readThemeAccent() {
  /* A DOM read inside an otherwise pure module, and it is a deliberate,
   * temporary trade. `pageTintForMode` is called from three components —
   * Shop/index.js, BrandPage.js, CollectionPage.js — all of which another
   * session is editing right now, and threading a fourth argument through
   * them is exactly how two agents clobber one index in this repo. So the
   * value is resolved here by default and can be INJECTED explicitly, which
   * is what every test does. When those three files are free, pass it from the
   * call sites and this default becomes dead. */
  if (typeof window === 'undefined' || !window.getComputedStyle) return null;
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (!root) return null;
  const raw = window.getComputedStyle(root).getPropertyValue('--color-orange').trim();
  if (!raw) return null;
  try {
    return parseColorStrict(raw);
  } catch (err) {
    return null;
  }
}

export function classicTintFromTheme(themeRgb) {
  if (!themeRgb) return { ...CLASSIC_TINT };
  const hsl = rgbToHsl(themeRgb);
  if (!hsl || !Number.isFinite(hsl.h)) return { ...CLASSIC_TINT };
  return {
    h: Math.round(hsl.h),
    // The same ceiling the photograph sampler uses. A theme accent is a full
    // strength brand colour; at its own saturation the furniture shouts.
    s: Math.round(clamp(hsl.s, 0, TINT_MAX_SATURATION)),
    l: 100,
  };
}

/**
 * The tint the page should actually be painted, for a mode.
 *
 * `null` means "no tint resolved" and is passed straight through for FULL and
 * LIGHT — the caller publishes nothing and the theme-colour CSS fallback
 * governs, which is the existing no-cover/tainted-canvas path.
 *
 * CLASSIC never returns null. A classic page is a page, not the absence of an
 * opinion: falling through to the fallback would paint the theme-colour wash
 * that this mode exists to switch off.
 */
export function pageTintForMode(tint, mode, themeRgb = null) {
  const resolved = resolvePageBackgroundMode(mode);
  // `themeRgb` is the salon's own accent, read off the live document by
  // `useTintVars`. Absent (SSR, a test that passes two arguments, a theme that
  // never resolved) it falls back to the classic constant, so no caller breaks.
  if (resolved === 'CLASSIC') {
    /* NEUTRAL, NOT THE SHOP'S OWN HUE.
     *
     * This line used to read `classicTintFromTheme(...)`, which took the
     * business's accent hue at up to TINT_MAX_SATURATION and pinned only the
     * PAGE to l:100. Every surface below the page is solved against the ground,
     * so the hue came with them: on a bronze shop the soft backgrounds and the
     * category pills all came out beige, and the owner's reading of it was
     * exact -- "this specific color makes things look like old... with the
     * white style that we had before it was nicer".
     *
     * CLASSIC means the page from before the tint landed, and that page was
     * neutral: #FFFFFF over #F7F7F7 with an #ECEEF3 hairline. The accent still
     * appears -- on the selected pill, links, prices, buttons -- it just does
     * not paint the furniture. `classicTintFromTheme` is kept and exported
     * because its own test pins the clamp, but the ground no longer calls it. */
    return { ...CLASSIC_TINT };
  }
  if (!tint) return null;
  if (resolved === 'LIGHT') {
    return {
      h: tint.h,
      s: Math.round(clamp(tint.s, 0, LIGHT_MAX_SATURATION)),
      l: LIGHT_LIGHTNESS,
    };
  }
  return tint;
}
